import React, { useState, useEffect } from 'react';
import { ModalWrapper, ButtonGroup } from 'components/molecules';
import { PopupProps } from 'react-popup-manager/dist/src';
import { Form, Button, Input, Select } from 'antd';
import { useRefCode, useCrmDetail } from 'hooks';
import { CompanySelectBox } from 'components/atoms'
import { IMaskInput } from "react-imask";
import { makeMaskPhoneNumber } from "utils/form-utils"

export interface CrmEditModalProps extends PopupProps {
    id: number;
    company_srl?: number;
    company_name?: string;
    fixCompany?: boolean;
    addCrm?: CrmSaveReqDto; 
}

const CrmEditModal = ({ isOpen, onClose, id, company_srl=0, company_name='', fixCompany = false, addCrm}: CrmEditModalProps): React.ReactElement => {
  /* ======================================================
   * states
   * ====================================================== */    
const [saveData, setSaveData] = useState<CrmSaveReqDto>({id:0});

  /* ======================================================
  * hooks
  * ====================================================== */
  const onUpdateCompleted = (id) => {
    console.log(saveData);
    onClose?.(
        true, 
        id,
        saveData.name,
        saveData.phone,
        saveData.email,
        saveData.tel,
        saveData.position,
    );
  } 

  const { refCodeList: positionTypeList } = useRefCode({field:'crm_position2', status_type: 'AV'}); 
  const { refCodeList: crmTypeList } = useRefCode({field:'crm_type', status_type: 'AV'});  
  const { crmInfo, crmInsert, crmUpdate } = useCrmDetail({id:id, onUpdateCompleted: onUpdateCompleted}); 

   /* ======================================================
   * variables
   * ====================================================== */
  const [form] = Form.useForm();
  const { TextArea } = Input;

   /* ======================================================
   * events
   * ====================================================== */
    const onCancel = () => {
        onClose?.(false);
    };

    const onSubmit = (e) => {

        setSaveData(
            {
                id: id,
                name: e.name,
                company_srl: e.company_srl,
                tel: e.tel,
                phone: e.phone,
                email: e.email,
                position: e.position,
                department: e.department,
                position2: e.position2,
                fax: e.fax,
                address: e.address,
                crm_type: e.crm_type,
                memo: e.memo,
              }  
        );
        if (id > 0) {
            crmUpdate({
                id: id,
                name: e.name,
                company_srl: e.company_srl,
                tel: e.tel,
                phone: e.phone,
                email: e.email,
                position: e.position,
                department: e.department,
                position2: e.position2,
                fax: e.fax,
                address: e.address,
                crm_type: e.crm_type,
                memo: e.memo,
              })
        } else {
            crmInsert({
                id: 0,
                name: e.name,
                company_srl: e.company_srl,
                tel: e.tel,
                phone: e.phone,
                email: e.email,
                position: e.position,
                department: e.department,
                position2: e.position2,
                fax: e.fax,
                address: e.address,
                crm_type: e.crm_type,
                memo: e.memo,
              })
        }
    };

  /* ======================================================
   * effects
   * ====================================================== */

    useEffect(() => {
        if (!!crmInfo) {
            form.setFieldsValue({
                name: crmInfo.name,
                company_srl: crmInfo.company_srl,
                company_name: crmInfo.company_name,
                tel: crmInfo.tel,
                phone: crmInfo.phone,
                email: crmInfo.email,
                position: crmInfo.position,
                department: crmInfo.department,
                position2: crmInfo.position2,
                fax: crmInfo.fax,
                address: crmInfo.address,
                crm_type: crmInfo.crm_type,
                memo: crmInfo.memo,
            });
        }
    }, [crmInfo]);

    useEffect(() => {
        if (company_srl > 0) {
            form.setFieldsValue({
                company_srl: company_srl,
                company_name: company_name,
                name: addCrm?.name,
                phone: addCrm?.phone,
                tel: addCrm?.tel,
                email: addCrm?.email,
                position: addCrm?.position,
                department: addCrm?.department,
            });
        }
    }, [company_name, company_srl]);    

    return (
        <ModalWrapper open={isOpen} onCancel={onCancel} width={550} title={id===0?'CRM 등록':'CRM 수정'} closable>
            <Form
                form={form}
                name="CrmInfo"
                layout="horizontal"  
                labelCol={{ flex: '120px' }}
                onFinish={onSubmit}
                style={{width:'100%'}}
                autoComplete="off"
            >
                {fixCompany ? (
                    <>
                    <Form.Item name="company_name" label={'고객사(기업명)'} rules={[{ required: true, message: '' }]}>
                        <Input type="text" disabled={true}/>  
                    </Form.Item>
                    <Form.Item name="company_srl" style={{display:'none'}}>
                        <Input type="text" disabled={true}/>  
                    </Form.Item>                    
                    </>
                ) : (
                    <Form.Item label={'고객사(기업명)'} rules={[{ required: true, message: '' }]}>
                        <CompanySelectBox required={true} form={form} company_srl={crmInfo?.company_srl||0} />
                    </Form.Item>                    
                )}
                  
                <Form.Item name="name" label={'이름'} rules={[{ required: true, message: '' }]}>
                    <Input type="text" placeholder='이름을 입력하세요'/>                    
                </Form.Item>  
                <Form.Item name="tel" label={'전화번호'}>
                    <Input placeholder='전화번호를 입력하세요' onChange={(e) => form.setFieldValue('tel', makeMaskPhoneNumber(e.target.value))} />
                </Form.Item>  
                <Form.Item name="phone" label={'핸드폰'}>
                    <IMaskInput mask={'000-0000-0000'} className={'ant-input'} placeholder='핸드폰번호를 입력하세요'/>                                        
                </Form.Item>  
                <Form.Item name="email" label={'이메일'}>
                    <Input type="text" placeholder='이메일을 입력하세요'/>
                </Form.Item>  
                <Form.Item name="position" label={'직급'}>
                    <Input type="text" placeholder='직급을 입력하세요'/>                    
                </Form.Item> 
                <Form.Item label={'부서'}>
                    <Form.Item name="department" style={{ width:'200px', display: 'inline-block', marginRight: '10px' }}>
                        <Input type="text" placeholder='부서를 입력하세요'/>                    
                    </Form.Item>   
                    <Form.Item name="position2" style={{ display: 'inline-block', width: '100px' }}>
                        <Select placeholder="직책레벨 선택">
                        {positionTypeList?.map((item) => {
                            return (<Select.Option key={item.name} value={item.name}>{item.value}</Select.Option>)
                        })}                        
                        </Select>                                        
                    </Form.Item>                    
                </Form.Item>                
                <Form.Item name="fax" label={'팩스'}>
                <Input placeholder='팩스번호를 입력하세요' onChange={(e) => form.setFieldValue('fax', makeMaskPhoneNumber(e.target.value))} />
                </Form.Item>  
                <Form.Item name="address" label={'주소'}>
                    <Input type="text" placeholder='주소를 입력하세요'/>                    
                </Form.Item> 
                <Form.Item name="crm_type" label={'담당자구분'} rules={[{ required: true, message: '' }]}>
                    <Select placeholder="담당자구분 선택">
                    {crmTypeList?.map((item) => {
                        return (<Select.Option key={item.name} value={item.name}>{item.value}</Select.Option>)
                    })}                        
                    </Select>                                        
                </Form.Item>                                       
                <Form.Item name="memo" label={'메모'}>
                    <TextArea rows={3} placeholder='메모를 입력하세요'/>                    
                </Form.Item>                  
                <ButtonGroup className={'buttonsWrapper'}>
                    <Button onClick={onCancel}>취소</Button>
                    <Button htmlType="submit"  type="primary">{id===0?'등록':'수정'}</Button>
                </ButtonGroup>                
            </Form>
        </ModalWrapper>  
    )    
};

export default React.memo(CrmEditModal);    